import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { SERVICES, SERVICE_AREAS } from "@/lib/constants";

const PAGES = [
  { key: "home", label: "Home Page" },
  ...SERVICES.map((s) => ({ key: `service:${s.slug}`, label: `Service — ${s.title}` })),
  ...SERVICE_AREAS.map((a) => ({ key: `area:${a.slug}`, label: `Area — ${a.city}` })),
];

export default function AdminContent() {
  const [page, setPage] = useState("home");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/content/${page}`).then((r) => setData(r.data)).catch(() => setData({ page }));
  }, [page]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/content/${page}`, {
        seo_title: data.seo_title || "", meta_description: data.meta_description || "",
        heading: data.heading || "", subheading: data.subheading || "", body: data.body || "", faqs: data.faqs || [],
      });
      toast.success("Content saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">SEO & Content</h1>
        <p className="text-muted-foreground">Edit page metadata and content blocks. Powers search & answer-engine visibility.</p>
      </div>

      <div>
        <Label className="font-semibold">Page</Label>
        <Select value={page} onValueChange={setPage}>
          <SelectTrigger className="mt-1.5 rounded-sm max-w-sm" data-testid="content-page"><SelectValue /></SelectTrigger>
          <SelectContent>{PAGES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!data ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="bg-card border border-border p-6 space-y-4">
          <div><Label className="font-semibold">SEO Title</Label><Input value={data.seo_title || ""} onChange={set("seo_title")} className="mt-1.5 rounded-sm" data-testid="content-seo-title" /><p className="text-xs text-muted-foreground mt-1">{(data.seo_title || "").length}/60 characters recommended</p></div>
          <div><Label className="font-semibold">Meta Description</Label><Textarea value={data.meta_description || ""} onChange={set("meta_description")} rows={2} className="mt-1.5 rounded-sm" data-testid="content-meta" /><p className="text-xs text-muted-foreground mt-1">{(data.meta_description || "").length}/160 characters recommended</p></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="font-semibold">Heading (H1)</Label><Input value={data.heading || ""} onChange={set("heading")} className="mt-1.5 rounded-sm" data-testid="content-heading" /></div>
            <div><Label className="font-semibold">Subheading</Label><Input value={data.subheading || ""} onChange={set("subheading")} className="mt-1.5 rounded-sm" data-testid="content-subheading" /></div>
          </div>
          <div><Label className="font-semibold">Body Content</Label><Textarea value={data.body || ""} onChange={set("body")} rows={5} className="mt-1.5 rounded-sm" data-testid="content-body" /></div>
          <Button onClick={save} disabled={saving} className="bg-primary rounded-sm font-bold" data-testid="content-save"><Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save Content"}</Button>
        </div>
      )}
    </div>
  );
}
